<div class="refund-status-container">
  <!-- Top Navigation Header -->
  <div class="status-header">
    <a routerLink="/home/gallery" class="back-link">
      <svg class="back-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
      </svg>
      Back to Gallery
    </a>
    <div class="header-title-row">
      <div>
        <h1 class="status-title">Refund Status</h1>
        <p class="status-subtitle">Track the review status and issued replacement vouchers for your refund requests.</p>
      </div>
      <a routerLink="/home/refund" class="btn btn-outline new-request-btn">
        ➕ New Request
      </a>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-state-card">
    <div class="spinner"></div>
    <p>Loading your refund requests...</p>
  </div>

  <!-- Empty State (No Requests) -->
  <div *ngIf="!isLoading && refundRequests.length === 0" class="card empty-state-card">
    <div class="empty-icon-wrap">
      <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#94a3b8" stroke-width="1.5">
        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
        <line x1="2" y1="10" x2="22" y2="10"></line>
      </svg>
    </div>
    <h2>No Refund Requests Found</h2>
    <p class="empty-desc">You have not submitted any refund requests yet. If you experienced a problem at one of our photobooths, you can request a replacement coupon below.</p>
    <a routerLink="/home/refund" class="btn btn-primary">Report an Issue</a>
  </div>

  <!-- Multi-Request Overview List (When user has multiple tickets and none selected) -->
  <div *ngIf="!isLoading && refundRequests.length > 1 && !selectedRequest" class="multi-list-wrap">
    <div class="list-section-header">
      <h2>Your Refund Requests ({{ refundRequests.length }})</h2>
      <p>Select a request to view details and check for issued coupon vouchers.</p>
    </div>

    <div class="requests-grid">
      <div 
        *ngFor="let req of refundRequests" 
        class="card request-summary-card" 
        (click)="selectRequest(req)">
        <div class="req-card-top">
          <span class="req-id-pill">{{ req.ticketId || req.id }}</span>
          <span class="status status-partial" *ngIf="req.status === 'needs_review'">In Review</span>
          <span class="status status-paid" *ngIf="req.status === 'approved'">Approved</span>
          <span class="status status-not-paid" *ngIf="req.status === 'denied'">Denied</span>
          <span class="status status-sent" *ngIf="req.status === 'needs_more_info'">More Info Needed</span>
        </div>

        <div class="req-card-body">
          <div class="req-main-info">
            <span class="req-reason">{{ req.reasonCategory }}</span>
            <span class="req-machine" *ngIf="req.deviceId">{{ req.deviceId }}</span>
          </div>
          <div class="req-sub-info">
            <span>Session Date: <strong>{{ req.sessionDate || '-' }}</strong></span>
            <span>Amount: <strong>{{ req.originalPaidAmount | currency }}</strong></span>
          </div>
        </div>

        <div class="req-card-footer">
          <span class="submitted-date">Submitted {{ formatDate(req.createdAt) }}</span>
          <span class="view-link">View Details →</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Single Request Detail View -->
  <div *ngIf="!isLoading && selectedRequest" class="detail-view-wrap">
    <!-- Back to multi list if multiple requests exist -->
    <div class="detail-nav-bar" *ngIf="refundRequests.length > 1">
      <button type="button" class="back-list-btn" (click)="clearSelection()">
        ← Back to All Requests ({{ refundRequests.length }})
      </button>
    </div>

    <div class="card detail-main-card">
      <!-- Status Banner -->
      <div 
        class="status-alert-banner"
        [class.banner-approved]="selectedRequest.status === 'approved'"
        [class.banner-review]="selectedRequest.status === 'needs_review'"
        [class.banner-denied]="selectedRequest.status === 'denied'"
        [class.banner-info]="selectedRequest.status === 'needs_more_info'">
        <div class="status-alert-icon">
          <span *ngIf="selectedRequest.status === 'approved'">✓</span>
          <span *ngIf="selectedRequest.status === 'needs_review'">⏳</span>
          <span *ngIf="selectedRequest.status === 'denied'">✕</span>
          <span *ngIf="selectedRequest.status === 'needs_more_info'">💬</span>
        </div>
        <div class="status-alert-content">
          <div class="status-alert-title">
            <ng-container *ngIf="selectedRequest.status === 'approved'">Refund Approved & Coupon Issued</ng-container>
            <ng-container *ngIf="selectedRequest.status === 'needs_review'">Request Under Review</ng-container>
            <ng-container *ngIf="selectedRequest.status === 'denied'">Refund Request Denied</ng-container>
            <ng-container *ngIf="selectedRequest.status === 'needs_more_info'">Additional Information Required</ng-container>
          </div>
          <p class="status-alert-desc">
            <ng-container *ngIf="selectedRequest.status === 'approved'">
              Your replacement photobooth session coupon has been approved and issued below.
            </ng-container>
            <ng-container *ngIf="selectedRequest.status === 'needs_review'">
              Our customer support team is inspecting your session logs. You will receive an email once resolved.
            </ng-container>
            <ng-container *ngIf="selectedRequest.status === 'denied'">
              {{ selectedRequest.decisionRationale || 'This request did not meet our refund eligibility criteria.' }}
            </ng-container>
            <ng-container *ngIf="selectedRequest.status === 'needs_more_info'">
              {{ selectedRequest.adminInquiry || 'Please review our email inquiry and reply with requested transaction records.' }}
            </ng-container>
          </p>
        </div>
      </div>

      <!-- Issued Coupon Voucher Box (If Approved) -->
      <div *ngIf="selectedRequest.status === 'approved' && selectedRequest.couponCode" class="coupon-voucher-box">
        <div class="voucher-top">
          <div class="voucher-tag">🎫 REPLACEMENT PHOTOBOOTH VOUCHER</div>
          <div class="voucher-val font-bold text-green">Free Photobooth Session (100% OFF)</div>
        </div>

        <div class="coupon-code-container">
          <div class="code-display font-mono">{{ selectedRequest.couponCode }}</div>
          <button type="button" class="copy-btn" (click)="copyCouponCode(selectedRequest.couponCode)">
            {{ copiedCode ? '✓ Copied!' : '📋 Copy Code' }}
          </button>
        </div>

        <div class="voucher-instructions">
          <p>
            <strong>How to Redeem:</strong> Select <strong>"Coupon / Voucher"</strong> at the payment screen on any physical PhotoLavie terminal, then enter your 12-character code.
          </p>
        </div>
      </div>

      <!-- Ticket & Session Details Grid -->
      <div class="details-section-title">Ticket & Session Information</div>
      <div class="meta-grid">
        <div class="meta-item">
          <span class="meta-label">Ticket ID</span>
          <span class="meta-value font-mono">{{ selectedRequest.id }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Submitted On</span>
          <span class="meta-value">{{ formatDate(selectedRequest.createdAt) }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Booth / Machine ID</span>
          <span class="meta-value font-bold">{{ selectedRequest.deviceId || 'N/A' }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Store Location</span>
          <span class="meta-value">{{ selectedRequest.storeLocation || selectedRequest.deviceId || 'Not specified' }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Session ID</span>
          <span class="meta-value font-mono">{{ selectedRequest.sessionId || 'Manual Search' }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Picture Taken Date</span>
          <span class="meta-value font-bold text-blue">{{ selectedRequest.sessionDate || 'Recent' }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Payment Method</span>
          <span class="meta-value">{{ selectedRequest.originalPaymentMethod }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Original Paid Amount</span>
          <span class="meta-value font-bold">{{ selectedRequest.originalPaidAmount | currency }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Resolution Preference</span>
          <span class="meta-value font-bold" [class.text-amber]="selectedRequest.wantsCashRefund">
            {{ selectedRequest.wantsCashRefund ? '💵 Cash Refund Requested' : '🎫 Replacement Coupon' }}
          </span>
        </div>
      </div>

      <!-- Incident Reason & Description -->
      <div class="details-section-title">Incident Details</div>
      <div class="incident-card">
        <div class="incident-category-badge">{{ selectedRequest.reasonCategory }}</div>
        <p class="incident-text">
          {{ selectedRequest.reasonText || 'No additional details provided.' }}
        </p>
      </div>

      <!-- Attached Files Section -->
      <div *ngIf="evidenceManifests.length > 0 || isLoadingEvidence || selectedRequest.hasEvidence" class="attached-files-section">
        <div class="details-section-title">
          Attached Files ({{ evidenceManifests.length }})
        </div>

        <div *ngIf="isLoadingEvidence && evidenceManifests.length === 0" class="files-loading-hint">
          <div class="mini-spinner"></div>
          <span>Loading attached files...</span>
        </div>

        <div *ngIf="evidenceManifests.length > 0" class="attached-files-grid">
          <div 
            *ngFor="let file of evidenceManifests" 
            class="attached-file-card"
            (click)="openFileLightbox(file)">
            <div class="file-thumb-wrap">
              <img 
                *ngIf="evidenceUrls[file.id]" 
                [src]="evidenceUrls[file.id]" 
                [alt]="file.originalFilename" 
                class="file-thumb-img" />
              <div *ngIf="!evidenceUrls[file.id]" class="file-thumb-placeholder">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
              <div class="file-hover-overlay">
                <span class="hover-text">🔍 View</span>
              </div>
            </div>
            <div class="file-meta">
              <span class="file-name" [title]="file.originalFilename">{{ file.originalFilename }}</span>
              <span class="file-size" *ngIf="file.byteCount">{{ formatFileSize(file.byteCount) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Footer -->
      <div class="detail-actions-footer">
        <a routerLink="/home/refund" class="btn btn-outline">
          ➕ Report Another Issue
        </a>
        <a routerLink="/home/gallery" class="btn btn-primary">
          Return to My Gallery
        </a>
      </div>
    </div>
  </div>
</div>

<!-- Safe Lightbox Modal for Attached Files -->
<div *ngIf="activeLightboxUrl" class="lightbox-overlay" (click)="closeLightbox()">
  <div class="lightbox-modal" (click)="$event.stopPropagation()">
    <div class="lightbox-header">
      <div class="lightbox-title">{{ activeLightboxTitle || 'Uploaded File' }}</div>
      <button type="button" class="lightbox-close-btn" (click)="closeLightbox()">&times;</button>
    </div>
    <div class="lightbox-body">
      <img [src]="activeLightboxUrl" [alt]="activeLightboxTitle || 'Uploaded File Preview'" class="lightbox-full-img" />
    </div>
  </div>
</div>


